import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { formatDateTime } from '../lib/text';
import type { AppNotification } from '../lib/types';

// What Navaris noticed, for this person. The unread count rides on the workspace poll;
// the list is fetched when the bell opens. Opening one marks it read and follows its link.
export default function NotificationBell() {
  const { authFetch } = useAuth();
  const { unreadNotifications, refresh } = useWorkspace();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<AppNotification[] | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    const res = await authFetch('/notifications').catch(() => null);
    if (res?.ok) setItems((await res.json()).items ?? []);
  }, [authFetch]);

  useEffect(() => { if (open) load(); }, [open, load]);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const post = async (path: string) => {
    await authFetch(path, { method: 'POST' }).catch(() => null);
    await Promise.all([load(), refresh()]);
  };

  const openItem = (n: AppNotification) => {
    if (!n.read_at) post(`/notifications/${n.id}/read`);
    if (n.link) {
      setOpen(false);
      navigate(n.link);
    }
  };

  const label = unreadNotifications ? `Notifications - ${unreadNotifications} unread` : 'Notifications';

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={label}
        title={label}
        className={`relative w-8 h-8 rounded-md flex items-center justify-center text-offwhite transition-colors ${open ? 'bg-white/15' : 'hover:bg-white/10'}`}
      >
        <Bell className="w-[18px] h-[18px]" />
        {unreadNotifications > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-brand text-navy text-[10px] font-bold leading-4 text-center ring-2 ring-navy">
            {unreadNotifications > 9 ? '9+' : unreadNotifications}
          </span>
        )}
      </button>
      {open && (
        <div
          role="dialog"
          aria-label="Notifications"
          className="absolute right-0 top-full mt-2 w-96 max-w-[calc(100vw-1.5rem)] bg-white border border-softgrey rounded-lg shadow-lg z-40 text-graphite"
        >
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-softgrey">
            <span className="text-sm font-semibold">Notifications</span>
            {unreadNotifications > 0 && (
              <button
                type="button"
                onClick={() => post('/notifications/read-all')}
                className="text-xs text-brand-dark hover:underline"
              >
                Mark all read
              </button>
            )}
          </div>
          <ul className="max-h-[60vh] overflow-y-auto">
            {items === null ? (
              <li className="px-4 py-6 text-sm text-coolslate text-center">Loading…</li>
            ) : items.length === 0 ? (
              <li className="px-4 py-6 text-sm text-coolslate text-center">Nothing new.</li>
            ) : items.map(n => (
              <li key={n.id} className={`group relative border-b border-softgrey/60 last:border-b-0 ${n.read_at ? '' : 'bg-brand/5'}`}>
                <button
                  type="button"
                  onClick={() => openItem(n)}
                  className={`w-full text-left px-4 py-3 pr-10 hover:bg-offwhite transition-colors ${n.link ? '' : 'cursor-default'}`}
                >
                  <span className="flex items-start gap-2">
                    <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${n.read_at ? 'bg-transparent' : 'bg-brand'}`} aria-hidden />
                    <span className="min-w-0">
                      <span className={`block text-sm ${n.read_at ? 'text-graphite' : 'font-semibold text-graphite'}`}>{n.title}</span>
                      {n.body && <span className="block mt-0.5 text-xs text-coolslate leading-snug">{n.body}</span>}
                      <span className="block mt-1 text-[11px] text-coolslate">{formatDateTime(n.created_at)}</span>
                    </span>
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => post(`/notifications/${n.id}/dismiss`)}
                  aria-label={`Dismiss ${n.title}`}
                  title="Dismiss"
                  className="absolute top-2.5 right-2.5 w-6 h-6 rounded-md grid place-items-center text-coolslate hover:text-graphite hover:bg-softgrey/60"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
